import React from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import { motion } from "framer-motion";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import UploadDoc from "../NewApplication/UploadDoc";
import Loader from "../../components/Loader";
import { FiUpload, FiInfo } from "react-icons/fi";

const CredentialUpload = () => {
  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ["generatedRRR"],
    queryFn: async () => {
      const req = await axiosget("ms_application_fee");
      return req?.data;
    },
  });

  // Use the latest verified payment
  const verifiedPayment =
    payments && payments?.type === undefined
      ? payments.find((v) => v?.isverified === true)
      : null;

  const programmeId = verifiedPayment?.programme?._id;

  const { data: requirements, isLoading: reqLoading, error: reqError } = useQuery({
    queryKey: ["admissionRequirementforCourse", programmeId],
    queryFn: async () => {
      const req = await axiosget(`ms_programme_admission_requirement/${programmeId}`);
      return req?.data;
    },
    enabled: !!programmeId,
  });

  if (paymentsLoading) return <Loader />;

  if (!payments || payments?.type !== undefined || !verifiedPayment) {
    return (
      <div className="p-6">
        <Alert variant="warning">
          <AlertDescription>
            No verified payment found. Please complete and verify your application fee payment before uploading credentials.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/30 pb-12">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-neutral-800">Credential Upload</h1>
          <p className="text-sm text-neutral-500 mt-1">Upload your required documents below.</p>
        </div>

        <div className="space-y-6">
          {/* Programme Info */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <p className="text-sm text-neutral-500">Programme</p>
            <p className="text-base font-semibold text-neutral-900">
              {verifiedPayment?.programme?.code} - {verifiedPayment?.programme?.name}
            </p>
          </div>

          {/* Documents */}
          {reqLoading && <FormSkeleton fields={3} />}

          {reqError && (
            <Alert variant="danger">
              <AlertDescription>Error loading requirements: {reqError?.message || reqError}</AlertDescription>
            </Alert>
          )}

          {requirements?.type !== undefined && (
            <Alert variant="danger">
              <AlertDescription>{requirements?.message}</AlertDescription>
            </Alert>
          )}

          {requirements && requirements?.type === undefined && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiInfo className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900 mb-1">Document Upload Requirements</h4>
                    <p className="text-sm text-blue-700">
                      Please upload all required documents. Documents marked as optional can be skipped.
                    </p>
                  </div>
                </div>
              </div>

              {requirements?.list && requirements.list.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
                    <FiUpload className="h-4 w-4 text-primary-600" />
                    <span>Required Documents</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {requirements.list.map((r, i) => (
                      <UploadDoc r={r} key={`credential_upload_${i}`} />
                    ))}
                  </div>
                </div>
              )}

              {requirements?.continue === 0 ? (
                <Alert variant="success">
                  <AlertDescription>All documents uploaded successfully.</AlertDescription>
                </Alert>
              ) : (
                <Alert variant="info">
                  <AlertDescription>
                    {requirements?.continue > 0 && `${requirements.continue} required document(s) remaining.`}
                  </AlertDescription>
                </Alert>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialUpload;
